import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AlertController, LoadingController, ModalController, ToastController } from '@ionic/angular';
import { BaseClass } from 'src/app/shared/classes/base';
import { ProcessConfig } from 'src/app/shared/interfaces/process-config.interface';
import { ProcessStatusEnum } from 'src/app/shared/interfaces/process.interface';
import { UserPreview } from 'src/app/shared/interfaces/user-preview.interface';
import { TranslationService } from 'src/app/shared/services/translation.service';
import { UtilService } from 'src/app/shared/services/util.service';
import { GoalTypeEnum } from '../../shared/enums/GoalTypeEnum';
import { AuthService } from '../../shared/services/auth.service';
import { AttributeItem } from '../../users/_interfaces/users-admin';
import { Goal, GoalStatusEnum } from '../_interfaces/goal.interface';
import { SubordinateGoalStatus } from '../_interfaces/subordinate-goals-status.enum';
import { GoalsService } from '../goals.service';
import { GoalsInputFeedbackPage } from './goals-input-feedback/goals-input-feedback.page';
import { NewGoalsInput } from 'src/app/shared/modals/new-goals-input/new-goals-input';

@Component({
    selector: 'app-goals-input',
    templateUrl: './goals-input.page.html'
})
export class GoalsInputPage extends BaseClass {

    protected readonly GoalTypeEnum = GoalTypeEnum;

    public processConfig: ProcessConfig;
    public goals: Array<Goal>;
    public displayedGoals: Array<Goal> = [];
    public SubordinateGoalStatus = SubordinateGoalStatus;
    public manager: UserPreview;
    public selectedLanguage: string;
    public isConsentValid: boolean = false;
    public attributesUser: AttributeItem[];

    private _participantId: number;
    private _currentStatus: ProcessStatusEnum;

    private readonly _cycleId: number;
    private readonly _processId: number;

    constructor(
            protected _toastController: ToastController,
            protected _translationService: TranslationService,
            public alertController: AlertController,
            private _loadingController: LoadingController,
            private _modalController: ModalController,
            private _router: Router,
            private _goalsService: GoalsService,
            private _utilService: UtilService,
            private _authService: AuthService,
            private _activatedRoute: ActivatedRoute
    ) {
        super(_toastController, _translationService);

        this._processId = this._router.getCurrentNavigation().extras.state
                ? parseInt(this._router.getCurrentNavigation().extras.state.processId)
                :null;
        this._utilService.insertProcessIdInLocalStorage(this._processId);

        this._cycleId = parseInt(
                this._activatedRoute.snapshot.paramMap.get('cycleId'), 10
        );
    }

    ionViewWillEnter() {
        this.selectedLanguage = this._translationService.getSelectedLanguage();
        this._loadGoals(this._cycleId);
    }

    public getColumnSize(questionsCount: number): number {
        let tableSize = this.isConsentValid ? 12:10;
        if (this.processConfig) {
            tableSize = this.processConfig.title ? tableSize - 2:tableSize;
            tableSize = this.processConfig.weight ? tableSize - 2:tableSize;
            tableSize = this.processConfig.deadline ? tableSize - 2:tableSize;
            tableSize = this.processConfig.description ? tableSize - 2:tableSize;
        }

        let columnSize = questionsCount ? parseFloat((tableSize / questionsCount).toFixed(2).toString()):null;

        if (columnSize)
            return columnSize;
        return questionsCount > 1 ? 1:2;
    }

    public goBack(): void {
        this._utilService.removeProcessIdFromLocalStorage();
        this._router.navigate(['dashboard']);
    }

    ionViewDidLeave() {
        this._utilService.removeProcessIdFromLocalStorage();
    }

    public hasRejectGoals(): boolean {
        return this.goals && this.goals.some(goal => goal.status === GoalStatusEnum.Rejected);
    }

    public getTotalWeight(): number {
        return this.goals ? this.goals.reduce((sum, goal) => sum + (goal.weight ? goal.weight:0), 0):0;
    }

    public async viewRejectionFeedbacks(goals: Array<Goal> | undefined) {
        if (!goals) return;

        const modal = await this._modalController.create({
            component: GoalsInputFeedbackPage,
            cssClass: 'new-goals-input round',
            componentProps: {
                'goals': goals.filter(goal => goal.status === GoalStatusEnum.Rejected),
                'manager': this.manager
            }
        });

        return await modal.present();
    }

    public async confirmDeleteGoal(goal: Goal): Promise<void> {
        const alert = await this.alertController.create({
            message: this._translationService.getTranslation('goalProgressInfo.confirmDeleteGoal'),
            buttons: [{
                text: this._translationService.getTranslation('generic.no'),
                role: 'cancel',
                cssClass: 'danger'
            }, {
                text: this._translationService.getTranslation('generic.yes'),
                handler: () => {
                    this._deleteGoal(goal.id);
                }
            }]
        });

        await alert.present();
    }

    private _deleteGoal(goalId: number): void {
        this._goalsService.deleteGoal(goalId).subscribe(() => {
            this._loadGoals(this._cycleId);
            this.notify(
                    this._translationService.getTranslation(
                            'goalsInput.goalDeleteSuccess'
                    ), 'success'
            );

        }, (error) => this.notify(this.getErrorNotification(error), 'danger'));
    }

    public async openManageGoalsInputModal(goal: Goal = null) {
        if (!goal && this.goals.length >= this.processConfig.maximumGoalQuantity) {
            this.notify(
                    this._translationService.getTranslation(
                            'goalsInput.maxGoalsQuantityReached'
                    ), 'danger'
            );
            return;
        }

        const modal = await this._modalController.create({
            component: NewGoalsInput,
            cssClass: 'new-goals-input',
            componentProps: {
                'cycleParticipantId': this._participantId,
                'processConfig': this.processConfig,
                'goal': goal,
                'processType': this.ProcessType.GoalInputs,
            }
        });

        modal.onDidDismiss().then((dismissEvent) => {
            if (dismissEvent.data) {
                this._loadGoals(this._cycleId);
            }
        });

        return await modal.present();
    }

    public canAddGoal(): boolean {
        return this._currentStatus === ProcessStatusEnum.WaitingInput ||
                this._currentStatus === ProcessStatusEnum.Declined;
    }

    public canEditGoals(goals: Array<Goal>): boolean {
        return goals && goals.length > 0 &&
                (
                        (this._currentStatus !== ProcessStatusEnum.AllowedViewing &&
                                this._currentStatus !== ProcessStatusEnum.AllowedViewingWithWarnings)
                        ||
                        goals.some(goal =>
                                goal.status === GoalStatusEnum.Draft ||
                                goal.status === GoalStatusEnum.Rejected)
                );
    }

    public submitGoals(goals: Array<Goal>): void {
        if (this._validateWeights(goals)) {
            const goalsIds = goals.map(goal => goal.id);
            this._goalsService.submitGoals(
                    goalsIds, this._participantId
            ).subscribe(() => {
                this.notify(
                        this._translationService.getTranslation(
                                'goalsInput.sendGoalsSuccess'
                        ), 'success'
                );
                this.goBack();
            }, (error) => this.notify(this.getErrorNotification(error), 'danger'));
        }
    }

    public async submitConsentGoals(goals: Array<Goal>): Promise<void> {
        const alert = await this.alertController.create({
            message: this._translationService.getTranslation('goals.consentGoalsSubmitConfirmation'),
            buttons: [
                {
                    text: this._translationService.getTranslation('generic.no'),
                    role: 'cancel',
                    cssClass: 'danger'
                },
                {
                    text: this._translationService.getTranslation('generic.yes'),
                    handler: async () => {
                        const loading = await this._loadingController.create();
                        await loading.present();
                        await this.addNewGoal(goals);
                        let goalsToSubmit: Array<Goal> = [];
                        this._goalsService.getUserGoals(this._cycleId, this.processConfig.goalType).subscribe(
                                async (response) => {
                                    if (response.data) {
                                        goalsToSubmit = response.data;
                                        this.submitGoals(goalsToSubmit);
                                        await loading.dismiss();
                                    }
                                },
                                async (error) => {
                                    await loading.dismiss();
                                    await this.notify(this.getErrorNotification(error), 'danger');
                                }
                        );
                    }
                }
            ]
        });
        await alert.present();
    }

    private _validateWeights(goals: Array<Goal>): boolean {
        if (this.processConfig && goals.length < this.processConfig.minimumGoalQuantity) {
            this.notify(
                    this._translationService.getTranslation(
                            'goalsInput.MinimumGoalsNotReached'
                    ), 'danger'
            );
            return false;
        }
        if (this.processConfig && goals.length > this.processConfig.maximumGoalQuantity) {
            this.notify(
                    this._translationService.getTranslation(
                            'goalsInput.maxGoalsQuantityReached'
                    ), 'danger'
            );
            return false;
        }
        const weightSum = goals.reduce((sum, goal) => sum + goal.weight, 0);
        if (weightSum !== 100) {
            this.notify(
                    this._translationService.getTranslation(
                            'goalsInput.weightsWrongSum'
                    ), 'danger'
            );
            return false;
        }
        return true;
    }

    private async _loadGoals(cycleId: number): Promise<void> {
        const loading = await this._loadingController.create();
        await loading.present();

        this._goalsService.getUserGoalsForInput(
                cycleId
        ).subscribe((response) => {
            this._participantId = response.data.cycleParticipantId;

            this.processConfig = response.data.processConfig;
            this.attributesUser = response.data.attributesUser;
            this.processConfig.questions = this._utilService.orderByProperty(
                    this.processConfig.questions, 'order'
            );

            this.goals = response.data.goals;
            this.manager = {
                imageUrl: response.data.manager.imageUrl,
                username: response.data.manager.name
            };
            this.displayedGoals = response.data.goals;
            this._currentStatus = response.data.status;

            this._consentIsValid();

            loading.dismiss();
        }, (error) => {
            loading.dismiss();
            this.notify(this.getErrorNotification(error), 'danger');
        });
    }

    private

    private _consentIsValid() {
        const attributesUser = this.attributesUser;
        const emptyAttribute: AttributeItem[] = [];
        const isAttributeOnConsent = this.processConfig
        && this.processConfig.consentAttributes
                ? this.processConfig.consentAttributes
                        .filter(x => x.id)
                :emptyAttribute;

        const isAllAttributesOnUser = isAttributeOnConsent.every(attribute => {
            return attributesUser.some(userAttribute => userAttribute.id === attribute.id);
        });
        const allAttributesIncludedOnUser = attributesUser
                && isAttributeOnConsent
                && isAllAttributesOnUser;

        if (this.processConfig.goalType === GoalTypeEnum.Consent && allAttributesIncludedOnUser) {
            if (this.goals.length === 0) {
                this.displayedGoals = this.processConfig.consentGoals;
                this.goals = this.processConfig.consentGoals;
            }
            this.isConsentValid = true;
        }
    }


    public async addNewGoal(goals: Array<Goal>): Promise<void> {
        if (goals.length > 0) {
            for (const goal of goals) {
                goal.cycleParticipantId = this._participantId;
                goal.creationUserId = this._authService.getLoggedUserId();

                try {
                    await this._goalsService.addNewGoal(goal).toPromise();
                } catch (error) {
                    await this.notify(this.getErrorNotification(error), 'danger');
                }
            }
        } else {
            await this.notify(
                    this._translationService.getTranslation('generic.missingFields'),
                    'danger'
            );
        }
    }
}
